import { useAppContext } from "@ftdr/blueprint-components-react";
import { payment3_paymentmethodpb } from "@ftdr/payment3_paymentmethod_coordinator-js-client";
import {
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import React, { forwardRef, useEffect } from "react";
import {
  PaymentsClientContext,
  setIsLoading,
  usePaymentsClientContext,
  usePaymentsClientStateContext,
} from "../../../contexts/payments-client-context";
import {
  PaymentProcessorClientInternalRef,
  PaypalProcessorComponents,
  PaypalProcessorScriptProviderProps,
  ProcessPaymentPaypalRequestData,
} from "../types";
import { PayPalBraintreeButtons } from "./components";
import { ApplicationStateType } from "../../payments-component";
import { LoadingSpinner } from "./view-components";

export type PaypalProcessorProps = {
  applicationState: ApplicationStateType;
  components: PaypalProcessorComponents;
  cashFlow?: payment3_paymentmethodpb.CashFlow;
  customerId?: string;
};

export type PaypalProcessorInternalProps = {
  components: Omit<
    PaypalProcessorComponents,
    "ErrorBoundaryComponent" | "PaypalProcessorScriptProviderView"
  >;
};

export const PaypalProcessorInternal = forwardRef<
  PaymentProcessorClientInternalRef<ProcessPaymentPaypalRequestData>,
  PaypalProcessorInternalProps
>(({ components }, ref) => {
  const {
    appSettings: { localizedText },
  } = useAppContext();
  const [{ isPending, isRejected }] = usePayPalScriptReducer();
  const {
    applicationState: { styleType },
  } = usePaymentsClientContext();
  // const shouldDisplayErrorNotification = !state.error.isError || isRejected;
  const shouldDisplayErrorNotification = isRejected;
  return (
    <components.PaypalProcessorInternalView>
      {isPending ? (
        <components.LoadingSpinner styleType={styleType || "default"} />
      ) : (
        <>
          {shouldDisplayErrorNotification ? (
            <components.ErrorNotification
              message={
                isRejected ? localizedText(`BRAINTREE_SCRIPT_ERROR`) : undefined
              }
              data-testid="paypal-rejected-notification"
            />
          ) : (
            <PayPalBraintreeButtons
              ref={ref}
              data-testid="paypal-braintree-buttons"
              components={components}
            />
          )}
        </>
      )}
    </components.PaypalProcessorInternalView>
  );
});

export const PaypalProcessorScriptProvider = forwardRef<
  PaymentProcessorClientInternalRef<ProcessPaymentPaypalRequestData>,
  PaypalProcessorScriptProviderProps
>(({ components }, ref) => {
  const { state } = usePaymentsClientStateContext();

  const {
    createPaymentMethodRegistration,
    cashFlow,
    applicationState,
    dispatch,
  } = usePaymentsClientContext();

  const brand = applicationState?.formsSetup?.braintreeForm?.brand;
  const styleType = applicationState?.styleType;

  useEffect(() => {
    dispatch(setIsLoading(true));
    createPaymentMethodRegistration(
      {
        paymentMethod: {
          details: {
            type: payment3_paymentmethodpb.PaymentMethodType.PayPal,
            cashFlow,
          },
          ...(brand && { brand }),
        },
      },
      0
    );
    // eslint-disable-next-line
  }, [brand]);

  return (
    <components.PaypalProcessorScriptProviderView>
      {state?.isLoading ? (
        <LoadingSpinner styleType={styleType || "default"} />
      ) : (
        <>
          {state.error.isError ? (
            <components.ErrorNotification message={state.error.message} />
          ) : (
            <PayPalScriptProvider
              options={{
                "client-id":
                  state.clientSetup.registration?.braintree?.payPalClientID ||
                  "",
                "data-client-token":
                  state.clientSetup.registration?.braintree?.clientToken || "",
                "disable-funding": "card,credit,bancontact",
                "vault": true,
                "intent": "tokenize",
                "commit": false,
              }}
              data-testId="paypal-script-provider"
            >
              <PaypalProcessorInternal
                ref={ref}
                data-testId="paypal-processor-internal"
                components={components}
              />
            </PayPalScriptProvider>
          )}
        </>
      )}
    </components.PaypalProcessorScriptProviderView>
  );
});

export const PaypalProcessor = forwardRef<
  PaymentProcessorClientInternalRef<ProcessPaymentPaypalRequestData>,
  PaypalProcessorProps
>(({ applicationState, components, cashFlow }, ref) => {
  const {
    appSettings: { localizedText },
  } = useAppContext();

  return (
    <PaymentsClientContext
      localizedText={localizedText}
      applicationState={{
        ...applicationState,
        ...{
          state: {
            ...applicationState?.state,
            ...{
              internalState: {
                ...applicationState?.state?.internalState,
                ...{ activePaymentMethod: "PAYPAL" },
              },
            },
          },
        },
      }}
      cashFlow={cashFlow}
    >
      <components.ErrorBoundaryComponent>
        <PaypalProcessorScriptProvider ref={ref} components={components} />
      </components.ErrorBoundaryComponent>
    </PaymentsClientContext>
  );
});

PaypalProcessor.displayName = "PaypalProcessor";
PaypalProcessorScriptProvider.displayName = "PaypalProcessorScriptProvider";
PaypalProcessorInternal.displayName = "PaypalProcessorInternal";
